import { Linear, gsap } from "gsap";
import { Helper } from "../../utils/Helper";
import { BasicEngine } from "./BasicEngine";
import { IEngineCallbacks } from "./IEngine";

export interface IExpandAnimateConfig {
    targetSym: number;

    toSize: number;

    duration: number;
    ease?: any;
}

export class ReelEngine extends BasicEngine {
    constructor(callbacks: IEngineCallbacks, target: PIXI.Container) {
        super(callbacks, target);
    }

    public Start(duration: number = 0, ease?: any): gsap.core.Timeline {
        this.nextTimeline = Helper.setTimeline(
            this.nextTimeline,
            {
                paused: true,
                // onStart: () => Helper.triggerIfExist(this.callbacks.onStartCallBack),
                onComplete: () => this.onStartCompleted(),
            }
        );
        this.nextTimeline.add(() => {Helper.triggerIfExist(this.callbacks.onStartCallBack)},duration/2);
            
        
        this.nextTimeline.fromTo(
            this,
            { yPos: 0 },
            {
                duration: duration,
                ease: ease,
                yPos: this.stepDistance,
            },0
        );
        return this.nextTimeline;
    }

    public Step(stepDurr: number = 0): gsap.core.Timeline {
        this.nextTimeline = Helper.setTimeline(
            this.nextTimeline,
            {
                paused: true,
                onRepeat: () => this.onStepCb(),
                repeat: -1,
                repeatDelay: 0,
            }
        );

        this.nextTimeline.fromTo(
            this,
            { yPos: 0 },
            {
                duration: stepDurr,
                ease: Linear.easeNone as any,
                yPos: this.stepDistance,
            }
        );

        return this.nextTimeline;
    }

    public ChangeSymPosStep(stepDurr: number = 0): gsap.core.Timeline {
        this.nextTimeline = Helper.setTimeline(
            this.nextTimeline,
            {
                paused: true,
                onComplete: () => this.onStepCb(),
            }
        );

        this.nextTimeline.fromTo(
            this,
            { yPos: 0 },
            {
                duration: stepDurr,
                ease: Linear.easeNone as any,
                yPos: this.stepDistance,
            }
        );

        this._animateContainer.children.forEach((c, i) => {
            this.nextTimeline!.to(c!, { y: this.stepDistance * (i - 1), duration: stepDurr }, 0);
        });

        return this.nextTimeline;
    }

    protected onStepCb(): void {
        Helper.triggerIfExist(this.callbacks.onCompletedStepCallback);
    }

    protected onNudged(): void {
        this.yPos = 0;
        Helper.triggerIfExist(this.callbacks.onNudgedCallback);
    }

    public MergeSlam(steps: number = 5, duration: number = 0, ease?: any): gsap.core.Timeline {
        this.nextTimeline = Helper.setTimeline(
            this.nextTimeline,
            {
                paused: true,
                onComplete: () => this.onSpinStopCompleted(),
                onStart: () => {
                    this.yPos = -steps * this.stepDistance;
                },
            }
        );

        this.nextTimeline.to(this, {
            duration: duration,
            ease: ease,
            yPos: 0,
        });

        this.nextTimeline.add(() => Helper.triggerIfExist(this.callbacks.onStopCallback), 0);
        this.nextTimeline.add(() => this.onBounce(), duration * 0.6);

        return this.nextTimeline;
    }

    public Stop(duration: number = 0, ease?: any): gsap.core.Timeline {
        this.nextTimeline = Helper.setTimeline(
            this.nextTimeline,
            {
                paused: true,
                onComplete: () => this.onSpinStopCompleted(),
            }
        );

        this.nextTimeline.to(this, {
            duration: duration,
            ease: ease,
            yPos: 0,
        });

        this.nextTimeline.add(() => Helper.triggerIfExist(this.callbacks.onStopCallback), 0);
        this.nextTimeline.add(() => this.onBounce(), duration * 0.6);

        return this.nextTimeline;
    }

    public Idle(): gsap.core.Timeline {
        this.nextTimeline = Helper.setTimeline(this.nextTimeline);
        return this.nextTimeline;
    }

    protected onBounce(): void {
        Helper.triggerIfExist(this.callbacks.onBounceStartCallback);
    }

    protected onStartCompleted(): void {
        this.yPos = 0;
        Helper.triggerIfExist(this.callbacks.onCompletedStartCallback);
    }

    protected onSpinStopCompleted(): void {
        this.yPos = 0;
        Helper.triggerIfExist(this.callbacks.onCompletedStopCallback);
    }

    protected onNudgeCompleted(): void {
        this.yPos = 0;
        Helper.triggerIfExist(this.callbacks.onCompletedNudgeCallback);
    }

    public changeSizeDuringSpin(durr: number, defHeight: number, newHeight: number, cb: Function): gsap.core.Timeline {
        /*
         * -------------------------------------------------------------------------------------------------------
         */

        // Time to loop threw the symbols on the reel and give them the configData depending on their current positions.
        // let Tweens: gsap.core.Tween[] = [];
        this.nextTimeline = Helper.setTimeline(
            new gsap.core.Timeline({
                paused: true,
                onStart: () => {
                    cb();
                },
                onComplete: () => Helper.triggerIfExist(this.callbacks.onCompletedStepCallback),
            })
        );
        let absStep: number = Math.abs(this.stepDistance);

        // (absStep - (PunkSymbolData.DEFAULT_DIM.HEIGHT)) * 0.5 + (sym.symbolHeight + (this.config.DIM.PADDING_Y as number)) * (sym.posID[0]);

        for (let pos = 0; pos < this._animateContainer.children.length; pos++) {
            const sym: PIXI.Sprite = this._animateContainer.children[pos] as PIXI.Sprite;
            // const fromPosY: number = sym.position.y + defHeight;
            const toPos = (absStep - defHeight) * 0.5 + newHeight * (pos - 1);
            this.nextTimeline.to(
                sym.position,
                {
                    y: toPos,
                    ease: Linear.easeNone as any,
                    duration: durr,
                },
                0
            );
        }

        this.nextTimeline.fromTo(
            this,
            { yPos: 0 },
            {
                duration: durr,
                ease: Linear.easeNone as any,
                yPos: this.stepDistance,
            },
            0
        );

        // this.nextTimeline.add(Tweens);
        return this.nextTimeline;
    }

    public moveToProportionPosition(durr: number, ease: any, currentAmount: number, totalSize: number): gsap.core.Timeline {
        const restSize = totalSize / currentAmount;

        const nextOffset: number = restSize * 0.5;
        const baseOffset: number = this.stepDistance * 0.5;
        /*
         * -------------------------------------------------------------------------------------------------------
         */

        // Time to loop threw the symbols on the reel and give them the configData depending on their current positions.
        let Tweens: gsap.core.Tween[] = [];
        this.nextTimeline = Helper.setTimeline(
            new gsap.core.Timeline({
                paused: true,
                onComplete: () => Helper.triggerIfExist(this.callbacks.onCompletedStepCallback),
            })
        );

        let accPosition: number = -baseOffset;

        for (let pos = 0; pos < currentAmount; pos++) {
            const sym: PIXI.Sprite = this._animateContainer.children[pos + 1] as PIXI.Sprite;
            const fromPosY: number = sym.position.y;

            accPosition += nextOffset;

            if (durr == 0) sym.position.y = accPosition;
            else {
                Tweens.push(
                    gsap.fromTo(
                        sym.position,
                        { y: fromPosY },
                        {
                            y: accPosition,
                            ease: ease,
                            duration: durr,
                        }
                    )
                );
            }
            accPosition += nextOffset;
        }

        this.nextTimeline.add(Tweens);
        return this.nextTimeline;
    }

    public moveToProportionRestPosition(
        durr: number,
        ease: any,
        currentAmount: number,
        totalSize: number,
        SpecialSize: number
    ): gsap.core.Timeline {
        const restSize = totalSize / currentAmount;
        const specialPos = SpecialSize + totalSize;

        const nextOffset: number = restSize * 0.5;
        const baseOffset: number = this.stepDistance * 0.5;
        /*
         * -------------------------------------------------------------------------------------------------------
         */

        // Time to loop threw the symbols on the reel and give them the configData depending on their current positions.
        let Tweens: gsap.core.Tween[] = [];
        this.nextTimeline = Helper.setTimeline(
            new gsap.core.Timeline({
                paused: true,
                onComplete: () => Helper.triggerIfExist(this.callbacks.onCompletedExpanding),
            })
        );

        let accPosition: number = -baseOffset;

        for (let pos = 0; pos < currentAmount; pos++) {
            const sym: PIXI.Sprite = this._animateContainer.children[pos + 1] as PIXI.Sprite;
            const fromPosY: number = sym.position.y;

            accPosition += nextOffset;

            if (durr == 0) sym.position.y = accPosition;
            else {
                Tweens.push(
                    gsap.fromTo(
                        sym.position,
                        durr,
                        { y: fromPosY },
                        {
                            y: accPosition,
                            ease: ease,
                        }
                    )
                );
            }
            accPosition += nextOffset;
        }

        const sym: PIXI.Sprite = this._animateContainer.children[currentAmount + 1] as PIXI.Sprite;
        const fromPosY: number = sym.position.y;

        if (durr == 0) sym.position.y = specialPos / 2;
        else {
            Tweens.push(
                gsap.fromTo(
                    sym.position,
                    durr,
                    { y: fromPosY },
                    {
                        y: specialPos / 2,
                        ease: ease,
                    }
                )
            );
        }

        this.nextTimeline.add(Tweens);
        return this.nextTimeline;
    }

    public StartTeaser(durr: number, ease: any): gsap.core.Timeline {
        this.nextTimeline = Helper.setTimeline(
            this.nextTimeline,
            { paused: true }
        );

        this.nextTimeline.fromTo(
            this,
            { yPos: 0 },
            {
                duration: durr,
                ease: ease,
                yPos: this.stepDistance,
            }
        );

        return this.nextTimeline;
    }

    public ExpandSymbol(conf: IExpandAnimateConfig, totalAmount: number, amountOfSymbols: number): gsap.core.Timeline {

        
        const toSymbolSize: number = this.stepDistance * conf.toSize;
        const totalSize = this.stepDistance * totalAmount;

        const restSize = (totalSize - toSymbolSize) / (amountOfSymbols - 1);
        const baseOffset: number = this.stepDistance * 0.5;

        /*
         * -------------------------------------------------------------------------------------------------------
         */

        // Time to loop threw the symbols on the reel and give them the configData depending on their current positions.
        let Tweens: gsap.core.Tween[] = [];
        this.nextTimeline = Helper.setTimeline(
            new gsap.core.Timeline({
                paused: true,
                onComplete: () => Helper.triggerIfExist(this.callbacks.onCompletedExpanding),
            })
        );

        let accPosition: number = -baseOffset;

        for (let pos = 0; pos < amountOfSymbols; pos++) {
            const sym: PIXI.Sprite = this._animateContainer.children[pos + 1] as PIXI.Sprite;
            const isTarget: boolean = conf.targetSym == pos;
            const fromPosY: number = sym.position.y;

            accPosition += isTarget ? toSymbolSize * 0.5 : restSize * 0.5;

            if (conf.duration == 0) sym.position.y = accPosition;
            else {
                Tweens.push(
                    gsap.fromTo(
                        sym.position,
                        { y: fromPosY },
                        {
                            y: accPosition,
                            ease: conf.ease,
                            duration: conf.duration,
                        }
                    )
                );
            }
            accPosition += isTarget ? toSymbolSize * 0.5 : restSize * 0.5;
        }

        this.nextTimeline.add(Tweens);
        return this.nextTimeline;
    }

    public Nudge(
        repeat: number = 0,
        duration: number = 0.5,
        ease?: any,
        stepDistance?: number,
        onNudgedCallback: () => void = () => {}
    ): gsap.core.Timeline {
        this.nextTimeline = new gsap.core.Timeline({
            paused: true,
            repeat: repeat,
            onRepeat: () => {
                this.activeTimeline?.invalidate();
            },
            onComplete: () => this.onNudgeCompleted(),
        });

        const dist: number = stepDistance != undefined ? stepDistance : this.stepDistance;

        this.nextTimeline.to(this, {
            duration: duration,
            ease: ease,
            yPos: "+=" + dist,
        });

        this.nextTimeline.add(() => {
            this.onNudged();
            onNudgedCallback();
        });

        return this.nextTimeline;
    }
}
